import { Link } from "react-router-dom";
import { Star, Check, Clock, ArrowRight } from "lucide-react";
import { BookButton } from "@/components/common/BookButton";
import { cn } from "@/lib/utils";

export const TicketCard = ({ ticket, featured = false, className = "" }) => {
  const to = `/tickets/${ticket.slug}`;
  return (
    <div
      data-testid={`ticket-card-${ticket.slug}`}
      className={cn(
        "group flex flex-col overflow-hidden rounded-2xl border border-stone-line bg-white shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift",
        className
      )}
    >
      <Link to={to} className="relative block aspect-[16/10] overflow-hidden">
        <img
          src={ticket.cardImage}
          alt={ticket.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {ticket.badge && (
          <span className="absolute left-4 top-4 rounded-full bg-charcoal/85 px-3 py-1 text-xs font-medium uppercase tracking-wider text-white backdrop-blur">
            {ticket.badge}
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-6 md:p-7">
        <div className="flex items-center gap-2 text-sm text-slatey">
          <Star size={15} className="fill-gold text-gold" />
          <span className="font-medium text-charcoal">{ticket.rating}</span>
          <span className="text-stone-line">·</span>
          <span>{ticket.reviews.toLocaleString()} reviews</span>
        </div>

        <Link to={to}>
          <h3 className="mt-3 font-serif text-2xl font-medium leading-snug text-charcoal transition-colors group-hover:text-gold-dark">
            {ticket.name}
          </h3>
        </Link>

        <ul className="mt-4 space-y-2">
          {ticket.highlights.slice(0, 3).map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slatey">
              <Check size={16} className="mt-0.5 shrink-0 text-gold" strokeWidth={2} />
              <span>{h}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex items-center gap-1.5 text-sm text-slatey">
          <Clock size={15} className="text-gold" />
          <span>{ticket.duration}</span>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-6">
          <div>
            <span className="block text-xs uppercase tracking-wider text-slatey">From</span>
            <span className="font-serif text-3xl font-medium text-charcoal">
              €{ticket.price}
            </span>
          </div>
          <BookButton
            label="Book"
            size="sm"
            testId={`ticket-card-book-${ticket.slug}`}
          />
        </div>

        <Link
          to={to}
          data-testid={`ticket-card-details-${ticket.slug}`}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-charcoal hover:text-gold transition-colors"
        >
          View details
          <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

export default TicketCard;
